import { useCallback, useEffect, useRef, useState } from 'react'

interface UseInViewOptions {
    rootMargin?: string
    triggerOnce?: boolean
}

interface UseInViewResult {
    ref: (node: Element | null) => void
    inView: boolean
}

/**
 * Tracks whether an element is visible in the viewport using the IntersectionObserver API.
 *
 * Returns a callback `ref` to attach to the target element and an `inView` boolean
 * that updates when the element enters or leaves the viewport.
 *
 * @param options.rootMargin - Margin around the root (e.g. `"200px"`) to trigger early/late.
 * @param options.triggerOnce - If `true`, stays `true` after the first intersection and stops observing.
 *
 * @example
 * const { ref, inView } = useInView({ rootMargin: '200px', triggerOnce: true })
 * return <div ref={ref}>{inView && <ExpensiveContent />}</div>
 */
export function useInView(options?: UseInViewOptions): UseInViewResult {
    const { rootMargin, triggerOnce = false } = options ?? {}
    const [inView, setInView] = useState(false)
    const observerRef = useRef<IntersectionObserver | null>(null)
    const triggeredRef = useRef(false)

    const ref = useCallback(
        (node: Element | null) => {
            if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
            }

            if (triggerOnce && triggeredRef.current) {
                return
            }

            // Covers both null and undefined from React callback refs
            if (node == null) {
                setInView(false)
                return
            }

            if (typeof IntersectionObserver === 'undefined') {
                return
            }

            const observer = new IntersectionObserver(
                ([entry]) => {
                    const isVisible = entry.isIntersecting

                    if (isVisible) {
                        setInView(true)
                        if (triggerOnce) {
                            triggeredRef.current = true
                            observer.unobserve(entry.target)
                            observer.disconnect()
                        }
                    } else if (!triggerOnce || !triggeredRef.current) {
                        setInView(false)
                    }
                },
                { rootMargin },
            )

            observer.observe(node)
            observerRef.current = observer
        },
        [rootMargin, triggerOnce],
    )

    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [])

    return { ref, inView }
}
